import { useState, type MouseEvent } from 'react';
import { MoreHorizontal, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useRestoreComponent } from '../api/restoreComponent';
import { cn } from '@/lib/utils';

interface ComponentDeletedActionsProps {
  branchId: string;
  name: string;
  className?: string;
}

const ComponentDeletedActions = ({
  branchId,
  name,
  className,
}: ComponentDeletedActionsProps) => {
  const [open, setOpen] = useState(false);
  const { mutate: restoreComponent, isPending: isRestoring } =
    useRestoreComponent();

  const handleRestore = (e: MouseEvent) => {
    e.stopPropagation();
    restoreComponent(branchId, {
      onSuccess: () => setOpen(false),
      onError: err => {
        console.error('[restoreComponent]', err);
        window.alert(
          err?.message ||
            'Restore failed. Check the API/backend logs and try again.',
        );
      },
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="icon"
          className={cn(
            'h-7 w-7 shrink-0 rounded-full p-0',
            'border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)]',
            className,
          )}
          aria-label={`Actions for deleted ${name}`}
          onClick={e => e.stopPropagation()}>
          <MoreHorizontal className="h-4 w-4" strokeWidth={2} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-36 p-1"
        onClick={e => e.stopPropagation()}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-full justify-start gap-2 px-2 text-sm font-normal"
          disabled={isRestoring}
          onClick={handleRestore}>
          <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
          {isRestoring ? 'Restoring…' : 'Restore'}
        </Button>
      </PopoverContent>
    </Popover>
  );
};

export default ComponentDeletedActions;
